
import React, { useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Book, Star } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

export interface Topic {
  id: string;
  name: string;
}

interface TopicSelectionDialogProps {
  topics: Topic[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTopicSelect: (topicId: string) => void;
  selectedTopicId: string | null;
  isLoading?: boolean;
  onGoBack?: () => void;
}

const TopicSelectionDialog: React.FC<TopicSelectionDialogProps> = ({
  topics,
  open,
  onOpenChange,
  onTopicSelect,
  selectedTopicId,
  isLoading = false,
  onGoBack
}) => {
  // For debugging purposes
  useEffect(() => {
    console.log("TopicSelectionDialog rendered with topics:", topics);
    console.log("Selected topic ID:", selectedTopicId);
    console.log("Dialog open state:", open);
    console.log("Loading state:", isLoading);
  }, [topics, selectedTopicId, open, isLoading]);

  // Handle topic selection with confirmation in the UI
  const handleTopicSelect = (topicId: string) => {
    console.log("Topic selected in dialog:", topicId);
    onTopicSelect(topicId);
  };

  // Handle go back action
  const handleGoBack = () => {
    console.log("Go Back button clicked in topic dialog");
    if (onGoBack) {
      onGoBack();
    } else {
      // If no onGoBack handler provided, just close the dialog
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-gradient-to-b from-blue-50 to-purple-50 rounded-xl border-4 border-indigo-200 shadow-xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-center text-indigo-700 flex items-center justify-center">
            <Star className="h-6 w-6 mr-2 text-yellow-500" />
            Pick a Fun Topic!
            <Star className="h-6 w-6 ml-2 text-yellow-500" />
          </DialogTitle>
          <DialogDescription className="text-sm text-center text-indigo-600">
            Choose what you want to learn about today
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          {isLoading ? (
            <div className="flex justify-center flex-col items-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-b-transparent border-indigo-500"></div>
              <p className="mt-3 text-indigo-600">Loading fun topics...</p>
            </div>
          ) : topics.length === 0 ? (
            <div className="text-center py-8 bg-white rounded-lg p-6 border-2 border-dashed border-indigo-200">
              <Book className="h-12 w-12 text-indigo-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-indigo-700 mb-1">No topics available</h3>
              <p className="text-sm text-indigo-500">Try another category or choose regular questions</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[300px] pr-4">
              <RadioGroup value={selectedTopicId || undefined} onValueChange={handleTopicSelect}>
                <div className="space-y-4">
                  {topics.map(topic => (
                    <div
                      key={topic.id}
                      className={`flex items-center space-x-2 rounded-xl border-2 p-4 transition-all ${
                        selectedTopicId === topic.id
                          ? 'border-indigo-400 bg-indigo-100 shadow-md'
                          : 'border-indigo-100 hover:border-indigo-300 hover:bg-indigo-50/50'
                      }`}
                    >
                      <RadioGroupItem 
                        value={topic.id} 
                        id={`topic-${topic.id}`}
                        className="text-indigo-600"
                      />
                      <Label 
                        htmlFor={`topic-${topic.id}`} 
                        className="flex-grow cursor-pointer font-medium text-indigo-700 text-lg"
                      >
                        {topic.name}
                      </Label>
                      {selectedTopicId === topic.id && (
                        <Check className="h-5 w-5 text-indigo-500" />
                      )}
                    </div>
                  ))}
                </div>
              </RadioGroup>
            </ScrollArea>
          )}
        </div>
        
        <DialogFooter className="flex flex-col sm:flex-row gap-3 sm:justify-between">
          <Button
            variant="outline"
            onClick={handleGoBack}
            className="rounded-xl border-2 border-pink-200 text-pink-600 hover:bg-pink-50"
          >
            Go Back
          </Button>
          <Button
            onClick={() => onOpenChange(false)}
            disabled={!selectedTopicId || isLoading}
            className="rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white font-medium"
          >
            Start Learning!
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TopicSelectionDialog;
